/**
 * EmailTemplateEditor
 *
 * Create / edit email templates for invoice sending.
 *
 * Layout mirrors the InvoiceTemplateBuilder:
 *   - Full-page takeover (negative margin, viewport height)
 *   - Two-column: left = editor form, right = TokenSidePanel
 *   - MergeFieldEditor (Tiptap) for the body — renders merge chips inline
 *   - Plain Input for the subject — tokens inserted at cursor via ref
 *   - Preview tab shows the email rendered with sample data
 *
 * TODO (Engineering): Replace mock data and localStorage persistence with
 * real API calls to /api/email-templates when the backend is ready.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Eye, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MergeFieldEditor } from '@/components/ui/MergeFieldEditor';
import { TokenSidePanel } from '@/components/ui/TokenSidePanel';
import type { TokenGroup } from '@/components/ui/TokenSidePanel';
import type { MergeToken } from '@/services/customerTermsService';
import { EmailAddressInput } from '@/components/billing/EmailAddressInput';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type EmailTemplateType = 'invoice' | 'resend' | 'credit_note' | 'reminder';

export interface EmailTemplate {
  id: string;
  name: string;
  type: EmailTemplateType;
  /** Default To recipients — may include merge tokens like {client.email} */
  to: string[];
  /** Default CC recipients — may include merge tokens or literal addresses */
  cc: string[];
  /** Default BCC recipients — applied at send time (also editable per-send) */
  bcc: string[];
  subject: string;
  body: string;        // plain text (subject uses raw token strings)
  bodyHtml: string;    // rich HTML from MergeFieldEditor (with chip spans)
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ─────────────────────────────────────────────────────────────────────────────
// Email-specific merge tokens
// These extend the base ALL_MERGE_TOKENS with email-only fields.
// TODO (Engineering): Merge with backend token registry when available.
// ─────────────────────────────────────────────────────────────────────────────

export const EMAIL_MERGE_TOKENS: MergeToken[] = [
  // Client
  { token: '{client.name}',             label: '[Client Name]',          category: 'client',  description: 'The name of the client being invoiced' },
  { token: '{client.contactName}',      label: '[Contact Name]',         category: 'client',  description: 'Primary contact first + last name' },
  { token: '{client.contactFirstName}', label: '[Contact First Name]',   category: 'client',  description: 'Primary contact first name only' },
  { token: '{client.email}',            label: '[Client Email]',         category: 'client',  description: 'Primary billing email address' },
  { token: '{client.phone}',            label: '[Client Phone]',         category: 'client',  description: 'Primary contact phone number' },
  { token: '{client.accountNumber}',    label: '[Account Number]',       category: 'client',  description: 'Client account reference number' },
  { token: '{client.paymentTermsDays}', label: '[Payment Terms Days]',   category: 'client',  description: 'Days until payment is due (e.g. 30)' },
  // Invoice
  { token: '{invoice.number}',          label: '[Invoice Number]',       category: 'invoice', description: 'Invoice reference number (e.g. INV-2024-042)' },
  { token: '{invoice.date}',            label: '[Invoice Date]',         category: 'invoice', description: 'Date the invoice was issued' },
  { token: '{invoice.dueDate}',         label: '[Due Date]',             category: 'invoice', description: 'Date payment is due' },
  { token: '{invoice.total}',           label: '[Invoice Total]',        category: 'invoice', description: 'Total invoice amount including VAT' },
  { token: '{invoice.amountDue}',       label: '[Amount Due]',           category: 'invoice', description: 'Remaining balance due (after credits)' },

  // Project
  { token: '{project.name}',            label: '[Project Name]',         category: 'project', description: 'Name of the project being invoiced' },
  { token: '{project.code}',            label: '[Project Code]',         category: 'project', description: 'Internal project reference code' },
  // Company
  { token: '{company.name}',            label: '[Company Name]',         category: 'company', description: 'Your company name from Company Details' },
  { token: '{company.senderName}',      label: '[Sender Name]',          category: 'company', description: 'Name of the person sending the invoice' },
  { token: '{company.senderEmail}',     label: '[Sender Email]',         category: 'company', description: 'Reply-to email address for responses' },
];

// ─────────────────────────────────────────────────────────────────────────────
// TokenSidePanel groups derived from EMAIL_MERGE_TOKENS
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<MergeToken['category'], string> = {
  client:  'Customer Information',
  invoice: 'Invoice Details',
  project: 'Project Details',
  company: 'Company / Sender',
};

function buildTokenGroups(filterCategory?: string): TokenGroup[] {
  const categories = ['client', 'invoice', 'project', 'company'] as MergeToken['category'][];
  return categories
    .filter((cat) => !filterCategory || filterCategory === 'all' || cat === filterCategory)
    .map((cat) => ({
      label: CATEGORY_LABELS[cat],
      tokens: EMAIL_MERGE_TOKENS
        .filter((t) => t.category === cat)
        .map((t) => ({
          token: t.token,
          label: t.label.replace(/^\[|\]$/g, ''),
          description: t.description,
        })),
    }))
    .filter((g) => g.tokens.length > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock sample data for preview
// TODO (Engineering): Replace with real project/invoice context data
// ─────────────────────────────────────────────────────────────────────────────

const MOCK_PREVIEW_DATA: Record<string, string> = {
  '{client.name}':             'Acme Corporation',
  '{client.contactName}':      'James Whitfield',
  '{client.contactFirstName}': 'James',
  '{client.email}':            'james@acme.com',
  '{client.phone}':            '+44 20 7123 4567',
  '{client.accountNumber}':    'ACC-00042',
  '{client.paymentTermsDays}': '30',
  '{invoice.number}':          'INV-2024-042',
  '{invoice.date}':            '15 Jan 2025',
  '{invoice.dueDate}':         '14 Feb 2025',
  '{invoice.total}':           '£12,450.00',
  '{invoice.amountDue}':       '£12,450.00',

  '{project.name}':            'Website Redesign',
  '{project.code}':            'PRJ-0042',
  '{company.name}':            'Horizon Group Ltd',
  '{company.senderName}':      'Sarah Johnson',
  '{company.senderEmail}':     'sarah@horizongroup.com',
};

function resolveTokensInText(text: string): string {
  return Object.entries(MOCK_PREVIEW_DATA).reduce(
    (acc, [token, value]) => acc.replaceAll(token, value),
    text,
  );
}

/**
 * Normalise body HTML so blank lines survive dangerouslySetInnerHTML rendering.
 *
 * 1. Empty `<p></p>` tags (Tiptap's representation of blank lines) collapse in
 *    the browser because they have zero content height and adjacent margins
 *    collapse.  Injecting a `<br>` gives them height.
 * 2. If the content has no block-level HTML at all (plain text from the API
 *    with `\n` characters), convert newlines to `<br>` tags.
 */
function normaliseHtmlForPreview(html: string): string {
  let out = html;
  // Empty paragraphs → visible blank lines
  out = out.replace(/<p>\s*<\/p>/gi, '<p><br></p>');
  // Plain text (no block elements) — convert newlines to <br>
  if (!/<\/(?:p|div|br|h[1-6]|ul|ol|li|table|blockquote)\b/i.test(out)) {
    out = out.replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
  }
  return out;
}

/** Strip merge chip HTML spans and replace with resolved values for preview */
function resolveHtmlForPreview(html: string): string {
  // Replace <span data-merge-token="...">label</span> with the resolved value
  let resolved = html.replace(
    /<span[^>]*data-merge-token="([^"]+)"[^>]*>[^<]*<\/span>/g,
    (_, token) => MOCK_PREVIEW_DATA[token] ?? token,
  );
  resolved = normaliseHtmlForPreview(resolved);
  return resolved;
}

// ─────────────────────────────────────────────────────────────────────────────
// Type labels
// ─────────────────────────────────────────────────────────────────────────────

export const EMAIL_TEMPLATE_TYPE_LABELS: Record<EmailTemplateType, string> = {
  invoice:     'Invoice Email',
  resend:      'Resend Email',
  credit_note: 'Credit Note Email',
  reminder:    'Payment Reminder',
};

// ─────────────────────────────────────────────────────────────────────────────
// Default mock email templates (seed data)
// TODO (Engineering): Replace with GET /api/email-templates
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'et-001',
    name: 'Standard Invoice Email',
    type: 'invoice',
    // TODO (Engineering): Replace with values from GET /api/email-templates
    to: ['{client.email}'],
    cc: [],
    bcc: [],
    subject: 'Invoice {invoice.number} from {company.name}',
    bodyHtml: `<p>Dear <span data-merge-token="{client.contactFirstName}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Contact First Name]</span>,</p><p>Please find attached invoice <span data-merge-token="{invoice.number}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Invoice Number]</span> for <span data-merge-token="{project.name}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Project Name]</span>.</p><p><strong>Invoice Total:</strong> <span data-merge-token="{invoice.total}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Invoice Total]</span><br><strong>Payment Due:</strong> <span data-merge-token="{invoice.dueDate}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Due Date]</span></p><p>Kind regards,<br><span data-merge-token="{company.senderName}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Sender Name]</span><br><span data-merge-token="{company.name}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Company Name]</span></p>`,
    body: '',
    isDefault: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-06-15'),
  },
  // ── Resend Email template ────────────────────────────────────────────────
  // TODO (Engineering): Replace with GET /api/email-templates?type=resend
  // This template is pre-selected automatically when the ResendInvoiceDialog opens.
  {
    id: 'et-resend-001',
    name: 'Standard Resend Email',
    type: 'resend',
    // TODO (Engineering): Replace with values from GET /api/email-templates
    to: ['{client.email}'],
    cc: [],
    bcc: [],
    subject: 'Reminder: Invoice {invoice.number} from {company.name}',
    bodyHtml: `<p>Dear <span data-merge-token="{client.contactFirstName}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Contact First Name]</span>,</p><p>We are resending invoice <span data-merge-token="{invoice.number}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Invoice Number]</span> for your reference.</p><p><strong>Invoice Total:</strong> <span data-merge-token="{invoice.total}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Invoice Total]</span><br><strong>Payment Due:</strong> <span data-merge-token="{invoice.dueDate}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Due Date]</span></p><p>If you have any questions, please do not hesitate to get in touch.</p><p>Kind regards,<br><span data-merge-token="{company.senderName}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Sender Name]</span><br><span data-merge-token="{company.name}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Company Name]</span></p>`,
    body: '',
    isDefault: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-06-15'),
  },
  {
    id: 'et-002',
    name: 'Invoice Reminder',
    type: 'reminder',
    // TODO (Engineering): Replace with values from GET /api/email-templates
    to: ['{client.email}'],
    cc: [],
    bcc: [],
    subject: 'Payment Reminder — Invoice {invoice.number} (Due {invoice.dueDate})',
    bodyHtml: `<p>Dear <span data-merge-token="{client.contactFirstName}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Contact First Name]</span>,</p><p>This is a friendly reminder that invoice <span data-merge-token="{invoice.number}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Invoice Number]</span> for <span data-merge-token="{invoice.total}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Invoice Total]</span> was due on <span data-merge-token="{invoice.dueDate}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Due Date]</span>.</p><p>If you have already made payment, please disregard this message. Otherwise, we would appreciate your prompt attention.</p><p>Kind regards,<br><span data-merge-token="{company.senderName}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Sender Name]</span></p>`,
    body: '',
    isDefault: false,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-07-01'),
  },
  {
    id: 'et-003',
    name: 'Credit Note Notification',
    type: 'credit_note',
    // TODO (Engineering): Replace with values from GET /api/email-templates
    to: ['{client.email}'],
    cc: [],
    bcc: [],
    subject: 'Credit Note Issued — Reference {invoice.number}',
    bodyHtml: `<p>Dear <span data-merge-token="{client.contactFirstName}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Contact First Name]</span>,</p><p>Please find attached a credit note of <span data-merge-token="{invoice.total}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Invoice Total]</span> in relation to <span data-merge-token="{project.name}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Project Name]</span>.</p><p>This credit will be applied to your account accordingly. If you have any questions, please contact us at <span data-merge-token="{company.senderEmail}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Sender Email]</span>.</p><p>Kind regards,<br><span data-merge-token="{company.senderName}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">[Sender Name]</span></p>`,
    body: '',
    isDefault: false,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-08-10'),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Preview renderer
// ─────────────────────────────────────────────────────────────────────────────

function EmailPreview({
  subject,
  bodyHtml,
  to,
  cc,
  bcc,
}: {
  subject: string;
  bodyHtml: string;
  to: string[];
  cc: string[];
  bcc: string[];
}) {
  const resolvedSubject = resolveTokensInText(subject);
  const resolvedBodyHtml = resolveHtmlForPreview(bodyHtml);

  const resolveAddresses = (addrs: string[]) =>
    addrs.map((a) => resolveTokensInText(a)).join(', ');

  return (
    <div className="rounded-lg border bg-white overflow-hidden text-sm shadow-sm">
      {/* Email chrome */}
      <div className="bg-muted/40 border-b px-4 py-3 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-14 shrink-0">From:</span>
          <span className="text-xs">Sarah Johnson &lt;sarah@horizongroup.com&gt;</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-14 shrink-0">To:</span>
          <span className="text-xs">
            {to.length > 0
              ? resolveAddresses(to)
              : <span className="italic text-muted-foreground">No recipients</span>}
          </span>
        </div>
        {cc.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-14 shrink-0">CC:</span>
            <span className="text-xs">{resolveAddresses(cc)}</span>
          </div>
        )}
        {bcc.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-14 shrink-0">BCC:</span>
            <span className="text-xs">{resolveAddresses(bcc)}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-14 shrink-0">Subject:</span>
          <span className="text-xs font-medium">
            {resolvedSubject || <span className="text-muted-foreground italic">No subject</span>}
          </span>
        </div>
      </div>
      {/* Body */}
      <div
        className="p-5 prose prose-sm max-w-none text-foreground"
        dangerouslySetInnerHTML={{ __html: resolvedBodyHtml || '<p class="text-muted-foreground italic">No body content</p>' }}
      />
      {/* Legend */}
      <div className="border-t bg-warning-50 px-4 py-2">
        <p className="text-xs text-warning-700">
          ⚠ Preview uses sample data — merge fields will be replaced with real values when sent.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EmailTemplateEditor — main component
// ─────────────────────────────────────────────────────────────────────────────

interface EmailTemplateEditorProps {
  template: EmailTemplate | null; // null = create new
  onSave: (template: EmailTemplate) => void;
  onClose: () => void;
}

export function EmailTemplateEditor({
  template,
  onSave,
  onClose,
}: EmailTemplateEditorProps) {
  const isNew = !template?.id || template.id.startsWith('new-');

  const [name, setName]           = useState('');
  const [type, setType]           = useState<EmailTemplateType>('invoice');
  const [to, setTo]               = useState<string[]>([]);
  const [cc, setCc]               = useState<string[]>([]);
  const [bcc, setBcc]             = useState<string[]>([]);
  const [subject, setSubject]     = useState('');
  const [bodyHtml, setBodyHtml]   = useState('');
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');


  // Which field is "active" for token insertion from the side panel
  const [activeField, setActiveField] = useState<'subject' | 'body'>('body');
  const subjectRef = useRef<HTMLInputElement>(null);

  // We expose an insertion trigger for MergeFieldEditor via a callback ref
  const [bodyInsertToken, setBodyInsertToken] = useState<((token: string) => void) | null>(null);

  // Populate form when template changes
  useEffect(() => {
    setName(template?.name ?? '');
    setType(template?.type ?? 'invoice');
    setTo(template?.to ?? []);
    setCc(template?.cc ?? []);
    setBcc(template?.bcc ?? []);
    setSubject(template?.subject ?? '');
    setBodyHtml(template?.bodyHtml ?? template?.body ?? '');
    setActiveTab('edit');
    setActiveField('body');
  }, [template]);

  // Insert token from the side panel into the active field
  const handleInsertToken = useCallback((token: string) => {
    // Find the matching MergeToken for the chip label
    const mergeToken = EMAIL_MERGE_TOKENS.find((t) => t.token === token);
    const label = mergeToken ? mergeToken.label : token;

    if (activeField === 'subject' && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? subject.length;
      const end   = el.selectionEnd   ?? subject.length;
      const next  = subject.slice(0, start) + token + subject.slice(end);
      setSubject(next);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      }, 0);
    } else {
      // Delegate to MergeFieldEditor's insertToken via callback
      if (bodyInsertToken) {
        bodyInsertToken(token);
      }
    }
    void label; // suppress unused var warning
  }, [activeField, subject, bodyInsertToken]);

  const handleSave = () => {
    const now = new Date();
    const saved: EmailTemplate = {
      id:        template?.id ?? `et-${Date.now()}`,
      name:      name.trim() || 'Untitled Template',
      type,
      to,
      cc,
      bcc,
      subject,
      body:      '',
      bodyHtml,
      isDefault: template?.isDefault ?? false,
      createdAt: template?.createdAt ?? now,
      updatedAt: now,
    };
    onSave(saved);
  };

  const isDirty =
    name    !== (template?.name    ?? '') ||
    type    !== (template?.type    ?? 'invoice') ||
    JSON.stringify(to)  !== JSON.stringify(template?.to  ?? []) ||
    JSON.stringify(cc)  !== JSON.stringify(template?.cc  ?? []) ||
    JSON.stringify(bcc) !== JSON.stringify(template?.bcc ?? []) ||
    subject !== (template?.subject ?? '') ||
    bodyHtml !== (template?.bodyHtml ?? template?.body ?? '');

  const tokenGroups = buildTokenGroups();

  // The MergeFieldEditor needs availableTokens — use all email tokens
  const availableTokens = EMAIL_MERGE_TOKENS;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="border-b bg-background px-6 py-4 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose} className="gap-1.5">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isNew ? 'Create Email Template' : 'Edit Template'}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {isNew ? 'Build a new email template with merge fields' : 'Modify your template'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              variant="outline"
              onClick={() => setActiveTab(activeTab === 'preview' ? 'edit' : 'preview')}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button onClick={handleSave} disabled={!name.trim()}>
              <Save className="h-4 w-4 mr-2" />
              {isNew ? 'Create Template' : 'Save Changes'}
              {isDirty && !isNew && (
                <Badge variant="secondary" className="ml-2 text-xs py-0">Unsaved</Badge>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* Left — editor form */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'edit' | 'preview')}>
            {/* Tab header */}
            <div className="flex items-center justify-between mb-6">
              <TabsList>
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="preview">
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                  Preview
                </TabsTrigger>
              </TabsList>
            </div>

            {/* ── Edit tab ──────────────────────────────────────── */}
            <TabsContent value="edit" className="space-y-6 mt-0">

              {/* Template Details card */}
              <div className="rounded-lg border bg-card p-6 space-y-5">
                <div>
                  <h2 className="text-base font-semibold">Template Details</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Create an email template with rich text formatting
                  </p>
                </div>
                <Separator />

                {/* Name + Category on same row */}
                <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="space-y-1.5">
                    <Label htmlFor="et-name">Template Name</Label>
                    <Input
                      id="et-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Standard Invoice Email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="et-type">Category</Label>
                    <Select value={type} onValueChange={(v) => setType(v as EmailTemplateType)}>
                      <SelectTrigger id="et-type">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.entries(EMAIL_TEMPLATE_TYPE_LABELS) as [EmailTemplateType, string][]).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* To / CC / BCC */}
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="et-to">
                      To
                      <span className="ml-1.5 text-xs text-muted-foreground font-normal">
                        (use <code className="bg-muted px-1 rounded text-[11px]">{'{client.email}'}</code> to auto-fill from client record)
                      </span>
                    </Label>
                    <EmailAddressInput
                      id="et-to"
                      value={to}
                      onChange={setTo}
                      placeholder="e.g. {client.email} or billing@client.com"
                    />
                  </div>
                  <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="space-y-1.5">
                      <Label htmlFor="et-cc">
                        CC <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <EmailAddressInput
                        id="et-cc"
                        value={cc}
                        onChange={setCc}
                        placeholder="cc@example.com"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="et-bcc">
                        BCC <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <EmailAddressInput
                        id="et-bcc"
                        value={bcc}
                        onChange={setBcc}
                        placeholder="bcc@example.com"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Press <kbd className="bg-muted border rounded px-1 py-0.5 text-[11px]">Enter</kbd> or <kbd className="bg-muted border rounded px-1 py-0.5 text-[11px]">,</kbd> after each address to add it. BCC recipients are also editable at the point of sending.
                  </p>
                </div>

                {/* Subject */}
                <div className="space-y-1.5">
                  <Label htmlFor="et-subject">Subject Line</Label>
                  <Input
                    id="et-subject"
                    ref={subjectRef}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    onFocus={() => setActiveField('subject')}
                    placeholder="e.g. Invoice {invoice.number} from {company.name}"
                    className={cn(
                      activeField === 'subject' && 'ring-2 ring-primary/30',
                    )}
                  />
                  {subject && (
                    <p className="text-xs text-muted-foreground truncate">
                      Preview: {resolveTokensInText(subject)}
                    </p>
                  )}
                </div>
              </div>

              {/* Content card */}
              <div className="rounded-lg border bg-card p-6 space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Content</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Use the toolbar or the Variables panel to insert merge fields
                  </p>
                </div>
                <Separator />

                <MergeFieldEditorWithInsertCallback
                  content={bodyHtml}
                  onChange={setBodyHtml}
                  availableTokens={availableTokens}
                  onFocus={() => setActiveField('body')}
                  onInsertReady={setBodyInsertToken}
                  placeholder="Write your email here. Click a variable on the right to insert it at the cursor."
                  className="min-h-[300px]"
                />
              </div>
            </TabsContent>

            {/* ── Preview tab ──────────────────────────────────── */}
            <TabsContent value="preview" className="mt-0">
              <EmailPreview subject={subject} bodyHtml={bodyHtml} to={to} cc={cc} bcc={bcc} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right — TokenSidePanel */}
        {activeTab === 'edit' && (
          <TokenSidePanel
            groups={tokenGroups}
            onInsert={handleInsertToken}
            className="w-64 shrink-0 self-stretch"
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wrapper around MergeFieldEditor that exposes an imperative insert callback
// The MergeFieldEditor uses Tiptap internally — we need to bridge the
// side-panel click to the editor's insertContent command.
// ─────────────────────────────────────────────────────────────────────────────

interface MergeFieldEditorWithInsertCallbackProps {
  content: string;
  onChange: (html: string) => void;
  availableTokens: MergeToken[];
  onFocus?: () => void;
  onInsertReady: (fn: (token: string) => void) => void;
  placeholder?: string;
  className?: string;
}

function MergeFieldEditorWithInsertCallback({
  content,
  onChange,
  availableTokens,
  onFocus,
  onInsertReady,
  placeholder,
  className,
}: MergeFieldEditorWithInsertCallbackProps) {
  // We can't imperatively call into MergeFieldEditor's Tiptap instance from
  // outside, so we pass the token via a state prop and let the editor handle it.
  const [pendingToken, setPendingToken] = useState<MergeToken | null>(null);

  // Expose the insert function to the parent via onInsertReady.
  // NOTE: onInsertReady is a setState setter — we must wrap in () => fn
  // so React stores the function as state rather than treating it as an updater.
  useEffect(() => {
    const fn = (tokenStr: string) => {
      const mergeToken = availableTokens.find((t) => t.token === tokenStr);
      if (mergeToken) {
        setPendingToken(mergeToken);
      }
    };
    onInsertReady(() => fn);
  }, [onInsertReady, availableTokens]);

  return (
    <div onFocus={onFocus}>
      <MergeFieldEditorWithPendingToken
        content={content}
        onChange={onChange}
        availableTokens={availableTokens}
        pendingToken={pendingToken}
        onPendingTokenConsumed={() => setPendingToken(null)}
        placeholder={placeholder}
        className={className}
      />
    </div>
  );
}

// Inner component that watches pendingToken and triggers insertion
interface MergeFieldEditorWithPendingTokenProps {
  content: string;
  onChange: (html: string) => void;
  availableTokens: MergeToken[];
  pendingToken: MergeToken | null;
  onPendingTokenConsumed: () => void;
  placeholder?: string;
  className?: string;
}

function MergeFieldEditorWithPendingToken({
  content,
  onChange,
  availableTokens,
  pendingToken,
  onPendingTokenConsumed,
  placeholder,
  className,
}: MergeFieldEditorWithPendingTokenProps) {
  // We inject the token directly into the HTML content when a pending token arrives.
  // This is a simple append-to-current-content approach since Tiptap's
  // insertContent is only accessible from inside the editor.
  // TODO (Engineering): For a more polished experience, expose a Tiptap ref
  // and call editor.chain().focus().insertContent(...) directly.
  const prevPendingRef = useRef<MergeToken | null>(null);

  useEffect(() => {
    if (pendingToken && pendingToken !== prevPendingRef.current) {
      prevPendingRef.current = pendingToken;
      const chip = `<span data-merge-token="${pendingToken.token}" class="merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 select-none cursor-default mx-0.5" contenteditable="false">${pendingToken.label}</span>`;
      // Append chip before closing </p> of last paragraph, or append at end
      const updated = content.endsWith('</p>')
        ? content.slice(0, -4) + chip + '</p>'
        : content + chip;
      onChange(updated);
      onPendingTokenConsumed();
    } else if (!pendingToken) {
      // Reset ref so the same token can be inserted again
      prevPendingRef.current = null;
    }
  }, [pendingToken, content, onChange, onPendingTokenConsumed]);

  return (
    <MergeFieldEditor
      content={content}
      onChange={onChange}
      availableTokens={availableTokens}
      placeholder={placeholder}
      className={className}
    />
  );
}
